/**
 * Create Profile Dialog Component
 * 
 * Dialog for creating a profile for an orphaned auth.user.
 * Creates a minimal profile with auto-populated email.
 */

import { useState, useEffect } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Loader2, UserPlus } from 'lucide-react';
import { useLanguage } from '@/core/LanguageContext';

interface CreateProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  email: string;
  onSave: (data: { userId: string; email: string; fullName?: string }) => void;
  isPending: boolean;
}

export function CreateProfileDialog({
  open,
  onOpenChange,
  userId,
  email,
  onSave,
  isPending,
}: CreateProfileDialogProps) {
  const { t } = useLanguage();
  const [fullName, setFullName] = useState('');

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFullName('');
    }
  }, [open]);

  const handleSave = () => {
    onSave({
      userId,
      email,
      fullName: fullName.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            {t('superadmin.users.createProfileTitle', 'Create Profile')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'superadmin.users.createProfileDescription',
              'Create a profile entry for this user. The email is auto-populated from their auth account.'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="profile-email">
              {t('superadmin.users.emailLabel', 'Email')}
            </Label>
            <Input id="profile-email" value={email} disabled className="bg-muted" />
            <p className="text-xs text-muted-foreground">
              {t('superadmin.users.emailHint', 'Email is populated from the auth account')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile-name">
              {t('superadmin.users.nameLabel', 'Full Name')} ({t('common.optional', 'Optional')})
            </Label>
            <Input
              id="profile-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder={t('superadmin.users.namePlaceholder', 'e.g., John Smith')}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="industrial" onClick={handleSave} disabled={isPending}>
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('superadmin.users.createProfileButton', 'Create Profile')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
